import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { User, Mail, KeyRound, ArrowRight } from "lucide-react";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("hombre");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    if (!/^\d{4,12}$/.test(password)) {
      setError("La contraseña debe tener entre 4 y 12 dígitos numéricos.");
      return;
    }
    setLoading(true);
    try {
      await register({ name: name.trim(), email: email.trim().toLowerCase(), gender, password });
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos crear tu cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-8">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-500 text-white font-outfit text-lg mb-3">t</div>
          <h1 className="font-outfit text-3xl text-ink-900">Empezá tu viaje</h1>
          <p className="font-dmsans text-sm text-ink-600 mt-1">Solo tres datos. Sin fricción.</p>
        </div>

        <form onSubmit={onSubmit} className="tk-card space-y-4" data-testid="register-form">
          <div>
            <label className="tk-label mb-1.5 block">Nombre</label>
            <div className="relative">
              <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
              <input
                className="tk-input pl-11"
                placeholder="¿Cómo te llamás?"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={80}
                data-testid="register-name"
              />
            </div>
          </div>

          <div>
            <label className="tk-label mb-1.5 block">Email</label>
            <div className="relative">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
              <input
                type="email"
                className="tk-input pl-11"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email"
              />
            </div>
          </div>

          <div>
            <label className="tk-label mb-1.5 block">Género</label>
            <div className="grid grid-cols-2 gap-2" data-testid="register-gender-group">
              {[
                { id: "hombre", label: "Hombre" },
                { id: "mujer", label: "Mujer" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setGender(opt.id)}
                  className={`rounded-2xl border px-4 py-3 font-dmsans text-sm transition ${
                    gender === opt.id
                      ? "border-sage-500 bg-sage-50 text-sage-700"
                      : "border-sand-200 bg-white text-ink-900 hover:border-sage-500/40"
                  }`}
                  data-testid={`register-gender-${opt.id}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="tk-label mb-1.5 block">Contraseña (numérica)</label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="tk-input pl-11 tracking-[0.4em]"
                  placeholder="• • • •"
                  value={password}
                  onChange={(e) => setPassword(e.target.value.replace(/\D/g, ""))}
                  required
                  maxLength={12}
                  data-testid="register-password"
                />
              </div>
            </div>
            <div>
              <label className="tk-label mb-1.5 block">Repetir contraseña</label>
              <div className="relative">
                <KeyRound size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-600/60" />
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="tk-input pl-11 tracking-[0.4em]"
                  placeholder="• • • •"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))}
                  required
                  maxLength={12}
                  data-testid="register-confirm"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-wellness-critical bg-wellness-critical/5 border border-wellness-critical/20 rounded-2xl px-4 py-2.5" data-testid="register-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading} className="tk-btn-primary w-full" data-testid="register-submit">
            {loading ? "Creando cuenta…" : <>Crear cuenta <ArrowRight size={16} /></>}
          </button>

          <p className="text-center text-sm text-ink-600 pt-1">
            ¿Ya tenés cuenta?{" "}
            <Link to="/login" className="text-sage-600 hover:text-sage-700 font-medium" data-testid="register-login-link">
              Iniciar sesión
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
